import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { LoaderProvider } from '@openx/components-core/lib/Loader/LoaderContext.tsx';
import { getInitialPckValues } from '@openx/components-package/components/PackageForm/constants.ts';
import { useRelatedDeals } from '@openx/components-package/components/PackageForm/useRelatedDeals.ts';
import { packageToFormState } from '@openx/components-package/components/PackageForm/utils/formStateConverter.ts';
import {
  type DealFormData,
  type DealFormDataWithTargeting,
  targetingSectionDealFormSwitch,
} from '@openx/types/deal.ts';
import type { Package, PackageFormState } from '@openx/types/package.ts';
import cloneDeep from 'lodash/cloneDeep';
import { enqueueSnackbar } from 'notistack';
import { type JSX, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DealSavingInProgress } from '../components/DealSavingInProgress.tsx';
import { useIsLegacyAmazonDeal } from '../hooks/useIsLegacyAmazonDeal.ts';
import { useDealContext } from '../utils/context/DealContext.tsx';
import { DealFormProvider } from './DealForm.context.tsx';
import { DealForm } from './DealForm.tsx';
import type { LocationState } from './DealForm.types.ts';
import { useTargetablePckgs } from './hooks/useTargetablePckgs.tsx';

type DealFormPageProps = {
  id?: string;
  renderFields: () => ReactNode;
};

export const isDealFormDataWithTargeting = (data: DealFormDataWithTargeting): data is DealFormDataWithTargeting =>
  'targeting' in data && !!data.targeting;

export const DealFormPage = ({ id: editedDealId, renderFields }: DealFormPageProps): JSX.Element => {
  const {
    useDealFormSelector,
    useDealFormActions,
    useInitialValuesLazy,
    usePackageFetch,
    useDealFormSubmit,
    useThirdPartyPartnersFetch,
  } = useDealContext();

  const [dealIds, setDealIds] = useState<string[]>([]);
  const [accountUid, setAccountUid] = useState<string | null>(null);

  const { dealForm, defaultAccountUid } = useDealFormSelector();
  const { getDealFormData } = useDealFormActions();

  const locationState: LocationState = useLocation().state;
  const { t } = useTranslation();

  /** While presence of ID means edit mode, and locationState.deal.uid means edit mode after (after returning from another form),
   * locationState.dealId is used only along with useAsTemplate or useAsSharedTargeting */
  const dealId = editedDealId ?? locationState?.deal?.uid ?? locationState?.dealId;
  const isEdit = !!(editedDealId ?? locationState?.deal?.uid);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <onInit>
  useEffect(() => {
    getDealFormData(dealId);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional one time effect
  useEffect(() => {
    if (locationState?.dsInitialDealFormPreset) {
      enqueueSnackbar(t('Deal Form Updated with Your Selection from Deal Prompt.'), { variant: 'success' });
    }
  }, []);

  /**
   * When returning from the package form, the form might be initially valid.
   * This occurs when all values are valid and the form has been modified.
   */
  const [pckgUid, setPckgUid] = useState<string | null>(null);
  const [pckgIsViaInteraction, setPckgIsViaInteraction] = useState<boolean>(false);

  const relatedDeals = useRelatedDeals(pckgUid);

  const { pckg, loading: isPackageLoading } = usePackageFetch({
    package_uid: pckgUid,
  });

  /** While it's not fetching package directly - it does trigger the fetch via effects and hooks */
  const pckgFetch = useCallback((uid: string) => {
    setPckgIsViaInteraction(true);
    setPckgUid(uid);
  }, []);

  const pckgClear = useCallback((isViaInteraction = false) => {
    if (isViaInteraction) {
      setPckgIsViaInteraction(true);
    }

    setPckgUid(null);
  }, []);

  const initialPckgUid = useMemo(() => {
    /** Navigate FROM PackageList. (case when Package is usedAsTemplate or usedAsSharedTargeting). */
    if (locationState?.packageUid) {
      return locationState?.packageUid;
    }

    /**
     * Navigate FROM external form. (case when returning to deal form from other form, like Create Audience).
     * Currently, we do limit package fetch to a single call - therefor in the situation of returning from external form,
     * the "targeting" data in initialValues will not be deal's real initial targeting. However, in case of returning
     * the "initialValues.targeting" will not be used by the form.
     **/
    if (locationState?.deal) {
      return locationState?.deal?.package_uid;
    }

    return dealForm.currentDeal?.package_uid;
  }, [locationState?.deal, locationState?.packageUid, dealForm.currentDeal]);

  /** Load package in Deal-Edit mode */
  // biome-ignore lint/correctness/useExhaustiveDependencies: <This should trigger when deal is loaded, and package hasn't been loaded yet>
  useEffect(() => {
    if (pckgUid || pckgIsViaInteraction) {
      return;
    }

    /** value different from "undefined" means uid was determined */
    if (initialPckgUid !== undefined) {
      setPckgUid(initialPckgUid);
    }
  }, [initialPckgUid]);

  const [initialValues, setInitialValues] = useState<DealFormDataWithTargeting | null>(null);
  const [isInitialPckgPending, setIsInitialPckgPending] = useState(false);

  const getInitialValues = useInitialValuesLazy(dealForm, defaultAccountUid, pckgUid ? pckg : null);
  const initialPckgLoaded = !!initialPckgUid && (pckg?.uid === initialPckgUid || pckg?.id === initialPckgUid);

  /** Form is initialized without waiting for the package - targeting is applied once the package is loaded */
  useEffect(() => {
    if (initialValues || (initialPckgUid && !pckgUid && !pckgIsViaInteraction)) {
      return;
    }

    const maybeTemplateOrTargeting = !dealId || (dealId && !!dealForm?.currentDeal?.uid);

    const isNewDeal = !editedDealId && !initialPckgUid && maybeTemplateOrTargeting;
    const isNewDealWithPckg = !editedDealId && initialPckgUid && maybeTemplateOrTargeting;
    const isExistingDeal = editedDealId && !!dealForm?.currentDeal?.uid && !initialPckgUid;
    const isExistingDealWithPckg = editedDealId && !!dealForm?.currentDeal?.uid && initialPckgUid;

    /** While isNewDeal and isExistingDeal covers the normal operation, isNewDealWithPckg and isExistingDealWithoutPckg covers
     * the case of returning to Deal Form from external form (like Create Audience) */

    if (isNewDeal || isNewDealWithPckg || isExistingDeal || isExistingDealWithPckg) {
      setInitialValues(getInitialValues());
      setIsInitialPckgPending(!!initialPckgUid && !initialPckgLoaded && !pckgIsViaInteraction);
    }
  }, [
    initialPckgLoaded,
    getInitialValues,
    initialValues,
    dealId,
    dealForm?.currentDeal?.uid,
    initialPckgUid,
    editedDealId,
    pckgUid,
    pckgIsViaInteraction,
  ]);

  /** Rebuild initialValues with the package targeting once the deferred package is loaded */
  useEffect(() => {
    if (!isInitialPckgPending) {
      return;
    }

    if (pckgIsViaInteraction) {
      setIsInitialPckgPending(false);
      return;
    }

    if (initialPckgLoaded) {
      setInitialValues(getInitialValues());
      setIsInitialPckgPending(false);
    }
  }, [isInitialPckgPending, pckgIsViaInteraction, initialPckgLoaded, getInitialValues]);

  const isInitialPckgLoadFailed =
    isInitialPckgPending && pckgUid === initialPckgUid && !isPackageLoading && !initialPckgLoaded;

  const [pckgInitialValues, setPckgInitialValues] = useState<PackageFormState>();

  /** Calling pckgReset causes pckgInitialValues to be rewritten to formValues.targeting */
  const pckgReset = useCallback(() => {
    setPckgInitialValues(cloneDeep(pckgInitialValues));
    setPckgIsViaInteraction(true);
  }, [pckgInitialValues]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <pckg?.uid is sufficient to tell if package was changed as pckg object reference is changed on each render>
  useEffect(() => {
    if (!initialValues) {
      return;
    }

    if (!pckgIsViaInteraction && isDealFormDataWithTargeting(initialValues) && initialValues.targeting) {
      /** On initial-load, targeting is a part of deal initialValues */
      setPckgInitialValues(cloneDeep(initialValues.targeting));
      return;
    }

    if (!pckgUid) {
      setPckgInitialValues(getInitialPckValues());
      return;
    }

    if (pckgUid && pckgUid !== pckg?.uid && pckgUid !== pckg?.id) {
      return;
    }

    setPckgInitialValues(packageToFormState(pckg as Package));
  }, [pckgUid, pckg?.uid, pckg?.id, pckgIsViaInteraction, initialValues]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <json representation of locationState.deal is sufficient to tell if locationState.deal was changed>
  const pckgInitialValuesWithRestoration = useMemo(() => {
    if (!pckgInitialValues) {
      return pckgInitialValues;
    }

    if (pckgIsViaInteraction) {
      return { ...pckgInitialValues, account_uid: accountUid || pckgInitialValues?.account_uid || '' };
    }

    if (locationState?.deal && isDealFormDataWithTargeting(locationState?.deal)) {
      return locationState?.deal?.targeting;
    }

    return { ...pckgInitialValues, account_uid: accountUid || pckgInitialValues?.account_uid || '' };
  }, [pckgIsViaInteraction, JSON.stringify(locationState?.deal || ''), pckgInitialValues]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <json representation of locationState.deal is sufficient to tell if locationState.deal was changed>
  const initialValuesWithRestoration = useMemo(() => {
    if (!initialValues) {
      return;
    }

    return locationState?.deal ?? (initialValues as DealFormData);
  }, [JSON.stringify(locationState?.deal || ''), initialValues, pckgIsViaInteraction]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <trap pckg used to create initialValues>
  const pckgInitialMode = useMemo(() => {
    if (!initialValuesWithRestoration) {
      return;
    }

    if (pckg && !pckg?.is_curated && initialValuesWithRestoration.package_uid === pckg?.uid) {
      return targetingSectionDealFormSwitch.EXISTING_PACKAGE;
    } else if (pckg?.is_curated) {
      return targetingSectionDealFormSwitch.CURATED_PACKAGE;
    } else {
      return targetingSectionDealFormSwitch.UNIQUE_TARGETING;
    }
  }, [initialValuesWithRestoration, pckg?.uid]);

  const currentDeal = useMemo(() => dealForm.currentDeal, [dealForm.currentDeal]);
  const isLegacyAmazonDeal = useIsLegacyAmazonDeal(currentDeal?.created_date, currentDeal?.deal_participants);

  const isInitialized = dealId ? !!currentDeal && !!initialValues : !!initialValues;
  const isLoading = !isInitialized || dealForm.loading;

  const { submit, savingDeal } = useDealFormSubmit({
    isEdit,
    dealIds,
    editedDealId,
    currentDeal,
    pckg,
    initialValues,
    pckgInitialValues,
    relatedDeals,
    pckgUid,
    pckgFetch,
  });

  const onPckgNotAllowed = useCallback(() => pckgClear?.(true), [pckgClear]);
  const { targetablePckgsAreLoading, targetableCuratedPckgs, targetablePckgs, onAccountChange } = useTargetablePckgs({
    editedDealId,
    pckgUid: pckgUid,
    onPckgNotAllowed,
  });

  const { loading, mapById } = useThirdPartyPartnersFetch(accountUid);

  return (
    <LoaderProvider>
      {isLoading && !savingDeal && <Loader data-test="deal-form-loader" />}
      {savingDeal && <DealSavingInProgress />}
      {isInitialized && (
        <>
          {isEdit && isLegacyAmazonDeal && !isLoading && !savingDeal && (
            <Alert severity="warning">
              <Typography variant="body1">
                {t(
                  "Changes to an existing deal's name will not appear in Amazon DSP. Pricing changes will be reflected in bid requests but will not update the deal details displayed in Amazon DSP."
                )}
              </Typography>
            </Alert>
          )}
          <DealFormProvider
            pckg={pckg || null}
            pckgUid={pckgUid}
            pckgIsLoading={isPackageLoading}
            pckgIsInitialLoading={isInitialPckgPending}
            pckgInitialLoadFailed={isInitialPckgLoadFailed}
            pckgFetch={pckgFetch}
            pckgClear={pckgClear}
            pckgReset={pckgReset}
            pckgInitialValues={pckgInitialValuesWithRestoration as PackageFormState}
            pckgIsViaInteraction={pckgIsViaInteraction}
            pckgInitialMode={pckgInitialMode}
            dealInitialValues={initialValues}
            targetablePckgs={targetablePckgs}
            targetableCuratedPckgs={targetableCuratedPckgs}
            targetablePckgsAreLoading={targetablePckgsAreLoading}
            thirdPartyPartnerOptions={mapById}
            thirdPartyPartnerOptionsFetch={setAccountUid}
            thirdPartyPartnerOptionsLoading={loading}
          >
            <DealForm
              initialValues={initialValuesWithRestoration as DealFormDataWithTargeting}
              isEdit={isEdit}
              onSubmit={submit}
              renderFields={renderFields}
              onDealIdsChange={setDealIds}
              hidden={isLoading || savingDeal}
              onAccountChange={onAccountChange}
            />
          </DealFormProvider>
        </>
      )}
    </LoaderProvider>
  );
};
